import React, { useState, useEffect, useMemo } from 'react';
import './Finances.css';
import { db, auth } from '../firebase';
import { collection, getDocs, updateDoc, doc, getDoc, setDoc, query, where, orderBy } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, ComposedChart, Line } from 'recharts';
import dayjs from 'dayjs';
import Layout from './Layout';
import RevenueByCustomer from './RevenueByCustomer';
import { PAYMENT_API_BASE, fetchSettledBatches } from '../utils/authorizeBatches';

const API_BASE = PAYMENT_API_BASE;
const PULSEFLOW_API = 'https://pulseflow.vuebillboards.com';

const CAMPAIGN_APP_BASE = 'https://app.vuebillboards.com/campaign';

const getTakeoverArchiveIds = (order) => (
  [...new Set([
    order?.takeoverDspCampaignId,
    order?.id,
    order?.takeoverDspCampaignId ? `warmup-${order.takeoverDspCampaignId}` : '',
  ].map((id) => String(id || '').trim()).filter(Boolean))]
);

/** Pulseflow billed plays/spend for a takeover (same source as TakeoverDetail). */
const fetchPulseflowPlayStats = async (campaignIds) => {
  const ids = [...new Set((campaignIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
  const empty = { plays: 0, spend: 0 };
  if (!ids.length) return empty;

  const perId = await Promise.all(ids.map(async (id) => {
    const stats = { plays: 0, spend: 0 };
    try {
      const summaryRes = await fetch(
        `${PULSEFLOW_API}/api/archive/billing/summary?campaignId=${encodeURIComponent(id)}`
      );
      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        stats.plays = Number(summary.totalRecords || summary.totalPlayouts || 0);
        stats.spend = Number(summary.totalBilledDollars || summary.totalSpendDollars || 0);
      }
    } catch (e) {
      console.warn('Pulseflow play stats failed for', id, e);
    }
    return stats;
  }));

  return perId.reduce((best, cur) => {
    if (!cur) return best;
    if (cur.plays > best.plays || (cur.plays === best.plays && cur.spend > best.spend)) {
      return cur;
    }
    return best;
  }, empty);
};

const ADMIN_WALLET = {
  balances: `${API_BASE}/api/admin/wallets/balances`,
  ledger: (uid) => `${API_BASE}/api/admin/wallets/${encodeURIComponent(uid)}/ledger`,
  adjust: (uid) => `${API_BASE}/api/admin/wallets/${encodeURIComponent(uid)}/adjust`,
};

function formatWalletUsd(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n / 100);
}

function ledgerTypeLabel(t) {
  if (!t) return '—';
  const map = {
    deposit: 'Deposit',
    checkout_debit: 'Checkout (wallet)',
    checkout_split: 'Checkout (split)',
    campaign_budget_topup: 'Campaign budget (from wallet)',
    campaign_surplus_credit: 'Unused budget → wallet',
    admin_adjustment: 'Admin adjustment',
  };
  return map[t] || String(t).replace(/_/g, ' ');
}

async function paymentServerAuthHeaders() {
  const u = auth.currentUser;
  if (!u) throw new Error('Not signed in');
  const token = await u.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

const INVOICE_DUE_DAYS = 45;

function invoiceAmount(inv) {
  return Number(inv?.summary?.totalAmount || 0);
}

function getInvoiceDueDate(inv) {
  const end = inv?.periodEndDate || inv?.asOfDate;
  if (!end) return null;
  const d = new Date(`${end}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + INVOICE_DUE_DAYS);
  return d;
}

function getStoredInvoiceStatus(inv) {
  return String(inv?.status || 'open').toLowerCase();
}

/** Stored status, or computed past_due when open/draft and past due date. */
function getEffectiveInvoiceStatus(inv) {
  const stored = getStoredInvoiceStatus(inv);
  if (stored === 'paid' || stored === 'void' || stored === 'voided' || stored === 'past_due') {
    return stored;
  }
  const due = getInvoiceDueDate(inv);
  if (due && Date.now() > due.getTime() && (stored === 'open' || stored === 'draft')) {
    return 'past_due';
  }
  return stored;
}

function isOutstandingInvoiceStatus(status) {
  const s = String(status || '').toLowerCase();
  return s === 'open' || s === 'draft' || s === 'past_due';
}

function formatInvoiceStatusLabel(status) {
  const s = String(status || 'open').toLowerCase();
  if (s === 'open') return 'Open';
  if (s === 'paid') return 'Paid';
  if (s === 'draft') return 'Draft';
  if (s === 'past_due') return 'Past due';
  if (s === 'void' || s === 'voided') return 'Voided';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatInvoicePeriod(inv) {
  if (inv?.periodStartDate && inv?.periodEndDate) {
    return `${formatDate(inv.periodStartDate)} → ${formatDate(inv.periodEndDate)}`;
  }
  if (inv?.periodKey) return inv.periodKey;
  return inv?.invoiceNumber || inv?.invoiceId || '—';
}

function getDefaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6); // last 7 days including today
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function formatLocalDateOnly(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDefaultOverviewRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  return {
    startDate: formatLocalDateOnly(start),
    endDate: formatLocalDateOnly(end),
  };
}

/** Inclusive month keys (YYYY-MM) from startDate to endDate. */
function monthKeysBetween(startDate, endDate) {
  const start = new Date(`${String(startDate).slice(0, 10)}T00:00:00`);
  const end = new Date(`${String(endDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    months.push({
      key,
      label: cursor.toLocaleString(undefined, { month: 'short', year: 'numeric' }),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

const Finances = () => {
  const [tab, setTab] = useState('overview');
  const [batches, setBatches] = useState([]);
  const [overviewBatches, setOverviewBatches] = useState([]);
  const [unsettledTransactions, setUnsettledTransactions] = useState([]);
  const [overviewOrders, setOverviewOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [overviewRange, setOverviewRange] = useState(getDefaultOverviewRange());
  const [batchesView, setBatchesView] = useState('list');
  const [batchReceipts, setBatchReceipts] = useState({}); // { [batchId]: { received: true, receivedAt: iso } }

  const [walletRows, setWalletRows] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState('');
  const [walletScanUsersScanned, setWalletScanUsersScanned] = useState(null);
  const [walletScanTruncated, setWalletScanTruncated] = useState(false);
  const [walletLegacyProbeTruncated, setWalletLegacyProbeTruncated] = useState(false);
  const [selectedWalletUserId, setSelectedWalletUserId] = useState(null);
  const [walletLedgerLoading, setWalletLedgerLoading] = useState(false);
  const [walletLedger, setWalletLedger] = useState([]);
  const [walletLedgerMeta, setWalletLedgerMeta] = useState({});
  const [walletLookupUid, setWalletLookupUid] = useState('');
  const [adjustDirection, setAdjustDirection] = useState('credit');
  const [adjustUsd, setAdjustUsd] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);
  const [adjustMessage, setAdjustMessage] = useState('');

  // Auto-load on mount
  useEffect(() => {
    fetchBatches(getDefaultDateRange());
    fetchUnsettledTransactions();
    fetchBatchesForOverview(getDefaultOverviewRange());
    fetchOrdersForOverview(180);
    // eslint-disable-next-line
  }, []);

  // Fetch batches (Authorize.net rejects ranges > ~31 days — chunked in fetchSettledBatches)
  const fetchBatches = async (range = dateRange) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSettledBatches({
        startDate: range.startDate,
        endDate: range.endDate,
        apiBase: API_BASE,
      });
      if (data.success) {
        setBatches(data.batches);
        fetchBatchReceipts(data.batches);
        if (data.message) setError(data.message);
      } else {
        setBatches([]);
        setError(data.message || 'Failed to fetch batches');
      }
    } catch (err) {
      setBatches([]);
      setError('Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  // Fetch batches for overview via chunked Authorize.net settled batches (any date range)
  const fetchBatchesForOverview = async (range = overviewRange) => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const data = await fetchSettledBatches({
        startDate: range.startDate,
        endDate: range.endDate,
        apiBase: API_BASE,
      });
      if (data.success) {
        setOverviewBatches(data.batches);
        fetchBatchReceipts(data.batches);
        if (data.message) setOverviewError(data.message);
      } else {
        setOverviewBatches([]);
        setOverviewError(data.message || 'Failed to fetch overview batches');
      }
    } catch (err) {
      setOverviewBatches([]);
      setOverviewError('Failed to fetch overview batches');
    } finally {
      setOverviewLoading(false);
    }
  };

  const applyOverviewPreset = (preset) => {
    const end = new Date();
    let start;
    if (preset === '3m') start = new Date(end.getFullYear(), end.getMonth() - 2, 1);
    else if (preset === '12m') start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
    else if (preset === 'ytd') start = new Date(end.getFullYear(), 0, 1);
    else if (preset === '24m') start = new Date(end.getFullYear(), end.getMonth() - 23, 1);
    else start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
    const next = {
      startDate: formatLocalDateOnly(start),
      endDate: formatLocalDateOnly(end),
    };
    setOverviewRange(next);
    fetchBatchesForOverview(next);
  };

  // Load receipt flags for visible batches
  const fetchBatchReceipts = async (list = []) => {
    try {
      const entries = await Promise.all(list.map(async (b) => {
        const ref = doc(db, 'batchReceipts', String(b.batchId));
        const snap = await getDoc(ref);
        if (snap.exists()) return [String(b.batchId), snap.data()];
        return [String(b.batchId), null];
      }));
      const map = {};
      entries.forEach(([id, data]) => { if (data) map[id] = data; });
      setBatchReceipts(map);
    } catch (_) {
      // ignore
    }
  };

  const markBatchReceived = async (batch) => {
    try {
      const id = String(batch.batchId);
      await setDoc(doc(db, 'batchReceipts', id), {
        batchId: id,
        received: true,
        receivedAt: new Date().toISOString(),
        settlementTimeUTC: batch.settlementTimeUTC || null,
        settlementState: batch.settlementState || null,
      }, { merge: true });
      setBatchReceipts(prev => ({ ...prev, [id]: { ...(prev[id] || {}), received: true, receivedAt: new Date().toISOString() } }));
    } catch (e) {
      alert('Failed to mark received');
    }
  };

  // Fetch unsettled transactions
  const fetchUnsettledTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/unsettled-transactions`);
      const data = await response.json();
      if (data.success) {
        // Flatten billTo fields and normalize for UI/spec
        const normalized = (data.transactions || []).map(t => {
          const bill = t.billTo || {};
          return {
            ...t,
            firstName: t.firstName || bill.firstName || '',
            lastName: t.lastName || bill.lastName || '',
            email: t.email || bill.email || '',
            submitTimeUTC: t.submitTimeUTC || t.submitTimeLocal || t.submitTimeUTC,
          };
        });
        setUnsettledTransactions(normalized);
      } else {
        setUnsettledTransactions([]);
        setError(data.message);
      }
    } catch (err) {
      setUnsettledTransactions([]);
      setError('Failed to fetch unsettled transactions');
    } finally {
      setLoading(false);
    }
  };

  // Fetch recent orders for overview fallback/transactions graph
  const fetchOrdersForOverview = async (daysBack = 90) => {
    try {
      const snap = await getDocs(collection(db, 'orders'));
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - daysBack);
      const items = [];
      snap.docs.forEach(d => {
        const data = d.data() || {};
        let dt = null;
        if (data.date) {
          const tmp = new Date(data.date);
          if (!isNaN(tmp)) dt = tmp;
        }
        if (!dt && data.createdAt) {
          try {
            if (typeof data.createdAt.toDate === 'function') dt = data.createdAt.toDate();
            else dt = new Date(data.createdAt);
          } catch (_) {
            const t2 = new Date(data.createdAt);
            if (!isNaN(t2)) dt = t2;
          }
        }
        if (!dt) return;
        if (dt >= start && dt <= end) {
          items.push({
            date: dt,
            totalPrice: Number(data.totalPrice || 0),
            status: data.status,
            paymentStatus: data.paymentStatus,
          });
        }
      });
      setOverviewOrders(items);
    } catch (_) {
      setOverviewOrders([]);
    }
  };

  const fetchWalletBalances = async () => {
    setWalletLoading(true);
    setWalletError('');
    try {
      const headers = await paymentServerAuthHeaders();
      const res = await fetch(ADMIN_WALLET.balances, { headers });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setWalletRows(Array.isArray(data.wallets) ? data.wallets : []);
      setWalletScanUsersScanned(
        typeof data.usersScanned === 'number' && Number.isFinite(data.usersScanned) ? data.usersScanned : null
      );
      setWalletScanTruncated(!!data.truncated);
      setWalletLegacyProbeTruncated(!!data.legacyProbeTruncated);
    } catch (e) {
      setWalletRows([]);
      setWalletScanUsersScanned(null);
      setWalletScanTruncated(false);
      setWalletLegacyProbeTruncated(false);
      setWalletError(e instanceof Error ? e.message : 'Failed to load wallets');
    } finally {
      setWalletLoading(false);
    }
  };

  const fetchWalletLedgerForUser = async (uid) => {
    setSelectedWalletUserId(uid);
    setWalletLedgerLoading(true);
    setWalletError('');
    setAdjustMessage('');
    setWalletLedger([]);
    try {
      const headers = await paymentServerAuthHeaders();
      const url = `${ADMIN_WALLET.ledger(uid)}?limit=1200`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setWalletLedgerMeta({
        email: data.email ?? null,
        displayName: data.displayName ?? null,
        balanceCents: data.balanceCents,
      });
      setWalletLedger(Array.isArray(data.ledger) ? data.ledger : []);
    } catch (e) {
      setWalletError(e instanceof Error ? e.message : 'Failed to load ledger');
    } finally {
      setWalletLedgerLoading(false);
    }
  };

  const loadWalletLookup = async () => {
    const uid = walletLookupUid.trim();
    if (uid.length < 10) {
      setWalletError('Enter a valid Firebase user id (UID).');
      return;
    }
    await fetchWalletLedgerForUser(uid);
  };

  const submitWalletAdjust = async () => {
    const uid = selectedWalletUserId;
    if (!uid) {
      setWalletError('Select a user or look up a UID first.');
      return;
    }
    const usd = Number.parseFloat(String(adjustUsd || '').replace(/,/g, ''));
    if (!Number.isFinite(usd) || usd <= 0) {
      setWalletError('Enter a positive USD amount.');
      return;
    }
    const cents = Math.round(usd * 100);
    if (cents < 1) {
      setWalletError('Amount too small.');
      return;
    }
    const delta = adjustDirection === 'credit' ? cents : -cents;
    const reason = adjustReason.trim();
    if (reason.length < 8) {
      setWalletError('Reason must be at least 8 characters (audit trail).');
      return;
    }
    const verb = adjustDirection === 'credit' ? 'CREDIT' : 'DEBIT';
    const ok = window.confirm(
      `Confirm wallet ${verb}: ${formatWalletUsd(Math.abs(delta))} for user ${uid}?\n\nReason:\n${reason}`
    );
    if (!ok) return;

    setAdjustSubmitting(true);
    setWalletError('');
    setAdjustMessage('');
    try {
      const headers = await paymentServerAuthHeaders();
      const idem =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `adm-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      const res = await fetch(ADMIN_WALLET.adjust(uid), {
        method: 'POST',
        headers: { ...headers, 'Idempotency-Key': idem },
        body: JSON.stringify({ deltaCents: delta, reason }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setAdjustMessage(
        data.idempotentReplay ? 'No change (idempotent replay).' : data.message || 'Adjustment applied.'
      );
      setAdjustUsd('');
      setAdjustReason('');
      await fetchWalletBalances();
      await fetchWalletLedgerForUser(uid);
    } catch (e) {
      setWalletError(e instanceof Error ? e.message : 'Adjustment failed');
    } finally {
      setAdjustSubmitting(false);
    }
  };

  useEffect(() => {
    if (tab === 'wallets') fetchWalletBalances();
    if (tab === 'invoices') fetchDspInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ---- DSP Reconciliation ----
  const [reconcileMonth, setReconcileMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reconcileRows, setReconcileRows] = useState([]);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileError, setReconcileError] = useState('');
  const [reconcileFetched, setReconcileFetched] = useState(false);

  // ---- DSP monthly invoices (Firestore `invoices` collection) ----
  const [dspInvoices, setDspInvoices] = useState([]);
  const [dspInstanceNames, setDspInstanceNames] = useState({});
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('outstanding');
  const [invoiceDspSearch, setInvoiceDspSearch] = useState('');
  const [invoiceStatusUpdating, setInvoiceStatusUpdating] = useState(null);

  const fetchDspInstanceNames = async () => {
    try {
      const snap = await getDocs(collection(db, 'dspInstances'));
      const map = {};
      snap.docs.forEach((d) => {
        const data = d.data() || {};
        map[d.id] = data.dspInstanceName || data.name || data.companyName || d.id;
      });
      setDspInstanceNames(map);
    } catch (e) {
      console.warn('Failed to load dspInstances names:', e);
    }
  };

  const fetchDspInvoices = async () => {
    setInvoicesLoading(true);
    setInvoicesError('');
    try {
      await fetchDspInstanceNames();
      const snap = await getDocs(collection(db, 'invoices'));
      const rows = snap.docs
        .map((d) => {
          const data = d.data() || {};
          return {
            ...data,
            invoiceId: data.invoiceId || d.id,
          };
        })
        .filter((inv) => inv.type === 'dsp_monthly' || String(inv.invoiceId || '').startsWith('dsp_'))
        .sort((a, b) => {
          const pk = String(b.periodKey || '').localeCompare(String(a.periodKey || ''));
          if (pk !== 0) return pk;
          return String(a.dspInstanceId || '').localeCompare(String(b.dspInstanceId || ''));
        });
      setDspInvoices(rows);
    } catch (e) {
      setDspInvoices([]);
      setInvoicesError(e?.message || 'Failed to load DSP invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const updateInvoiceStatus = async (invoice, nextStatus) => {
    const invoiceId = String(invoice.invoiceId || '').trim();
    if (!invoiceId) return;

    const label = formatInvoiceStatusLabel(nextStatus);
    const amount = invoiceAmount(invoice);
    const dspLabel = dspInstanceNames[invoice.dspInstanceId] || invoice.dspInstanceId;
    const ok = window.confirm(
      `Set invoice ${invoice.invoiceNumber || invoiceId} (${dspLabel}, $${formatCurrency(amount)}) to "${label}"?`
    );
    if (!ok) return;

    setInvoiceStatusUpdating(invoiceId);
    setInvoicesError('');
    try {
      const actor = auth.currentUser?.email || auth.currentUser?.uid || 'admin';
      const now = new Date().toISOString();
      const updates = {
        status: nextStatus,
        statusUpdatedAt: now,
        statusUpdatedBy: actor,
      };
      if (nextStatus === 'paid') {
        updates.paidAt = now;
      }
      await updateDoc(doc(db, 'invoices', invoiceId), updates);
      setDspInvoices((prev) =>
        prev.map((inv) =>
          inv.invoiceId === invoiceId ? { ...inv, ...updates } : inv
        )
      );
    } catch (e) {
      setInvoicesError(e?.message || 'Failed to update invoice status');
    } finally {
      setInvoiceStatusUpdating(null);
    }
  };

  const dspInvoiceSummary = useMemo(() => {
    const byDsp = {};
    let totalOutstanding = 0;
    let outstandingCount = 0;
    let pastDueCount = 0;
    let openCount = 0;

    dspInvoices.forEach((inv) => {
      const effective = getEffectiveInvoiceStatus(inv);
      const amount = invoiceAmount(inv);
      const dspId = String(inv.dspInstanceId || 'unknown');

      if (!byDsp[dspId]) {
        byDsp[dspId] = {
          dspInstanceId: dspId,
          name: dspInstanceNames[dspId] || dspId,
          outstanding: 0,
          invoiceCount: 0,
          pastDueCount: 0,
        };
      }

      if (isOutstandingInvoiceStatus(effective)) {
        byDsp[dspId].outstanding += amount;
        byDsp[dspId].invoiceCount += 1;
        totalOutstanding += amount;
        outstandingCount += 1;
        if (effective === 'past_due') {
          byDsp[dspId].pastDueCount += 1;
          pastDueCount += 1;
        } else {
          openCount += 1;
        }
      }
    });

    const dspRows = Object.values(byDsp)
      .filter((r) => r.outstanding > 0)
      .sort((a, b) => b.outstanding - a.outstanding);

    return { totalOutstanding, outstandingCount, pastDueCount, openCount, dspRows };
  }, [dspInvoices, dspInstanceNames]);

  const filteredDspInvoices = useMemo(() => {
    const q = invoiceDspSearch.trim().toLowerCase();
    return dspInvoices.filter((inv) => {
      const effective = getEffectiveInvoiceStatus(inv);
      if (invoiceStatusFilter === 'outstanding' && !isOutstandingInvoiceStatus(effective)) return false;
      if (invoiceStatusFilter !== 'outstanding' && invoiceStatusFilter !== 'all') {
        if (effective !== invoiceStatusFilter) return false;
      }
      if (!q) return true;
      const dspId = String(inv.dspInstanceId || '').toLowerCase();
      const name = String(dspInstanceNames[inv.dspInstanceId] || '').toLowerCase();
      const num = String(inv.invoiceNumber || inv.invoiceId || '').toLowerCase();
      return dspId.includes(q) || name.includes(q) || num.includes(q);
    });
  }, [dspInvoices, dspInstanceNames, invoiceStatusFilter, invoiceDspSearch]);

  const fetchReconciliation = async (monthKey = reconcileMonth) => {
    setReconcileLoading(true);
    setReconcileError('');
    setReconcileRows([]);
    try {
      const [year, month] = monthKey.split('-').map(Number);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 1); // exclusive

      // Takeovers live in the `orders` collection. Campaigns are in a separate
      // `campaigns` collection and are never included here.
      // Match dashboard logic exactly:
      //   - only paymentStatus === 'captured' (same as dashboard revenue calc)
      //   - bucket by paymentTimestamp, fallback to createdAt (same as dashboard)
      //   - amount = paymentAmount || totalPrice (same field priority as dashboard)
      const ordersSnap = await getDocs(collection(db, 'orders'));
      const dspOrders = ordersSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => {
          if (!o.takeoverDspCampaignId) return false;
          if (!o.signName) return false; // exclude non-takeover order docs
          if (o.paymentStatus !== 'captured') return false; // only captured payments (matches dashboard)
          if (o.status === 'Canceled' || o.status === 'Rejected') return false; // exclude canceled/rejected orders
          // Use paymentTimestamp for month bucketing (matches dashboard), fall back to createdAt
          let dt = null;
          if (o.paymentTimestamp) {
            try { dt = typeof o.paymentTimestamp.toDate === 'function' ? o.paymentTimestamp.toDate() : new Date(o.paymentTimestamp); } catch (_) {}
          }
          if (!dt && o.createdAt) {
            try { dt = typeof o.createdAt.toDate === 'function' ? o.createdAt.toDate() : new Date(o.createdAt); } catch (_) {}
          }
          if (!dt) return false;
          return dt >= start && dt < end;
        });

      // DSP spend/plays come from Pulseflow billing archive (not Firestore takeovers/*/wins).
      const rows = await Promise.all(dspOrders.map(async (order) => {
        const campaignId = order.takeoverDspCampaignId;
        const stats = await fetchPulseflowPlayStats(getTakeoverArchiveIds(order));
        const dspSpend = Number.isFinite(stats.spend) ? stats.spend : 0;
        const winsCount = Number.isFinite(stats.plays) ? stats.plays : 0;
        // Use same field priority as dashboard
        const revenue = Number(order.paymentAmount || order.totalPrice || 0);
        const profit = revenue - dspSpend;
        // Display date: use the scheduled takeover date
        let dateStr = '—';
        if (order.date) {
          const d = new Date(order.date);
          dateStr = isNaN(d) ? String(order.date) : d.toLocaleDateString();
        }
        return {
          id: order.id,
          signName: order.signName || '—',
          date: dateStr,
          status: order.status || '—',
          revenue,
          dspSpend,
          profit,
          winsCount,
          campaignId,
        };
      }));

      rows.sort((a, b) => new Date(b.date) - new Date(a.date));
      setReconcileRows(rows);
      setReconcileFetched(true);
    } catch (e) {
      setReconcileError(e.message || 'Failed to fetch reconciliation data');
    } finally {
      setReconcileLoading(false);
    }
  };

  const reconcileTotals = reconcileRows.reduce((acc, r) => {
    acc.revenue += r.revenue;
    acc.dspSpend += r.dspSpend;
    acc.profit += r.profit;
    acc.wins += r.winsCount;
    return acc;
  }, { revenue: 0, dspSpend: 0, profit: 0, wins: 0 });

  // Removed payment reference search

  // Helper to update Firestore status for a transaction
  const updateFirestoreStatus = async (transactionId, newStatus) => {
    const collectionsToCheck = ['orders', 'campaigns'];
    for (const col of collectionsToCheck) {
      // Check paymentTransactionId
      const q1 = query(collection(db, col), where('paymentTransactionId', '==', String(transactionId)));
      const snap1 = await getDocs(q1);
      if (!snap1.empty) {
        for (const d of snap1.docs) {
          await updateDoc(doc(db, col, d.id), { paymentStatus: newStatus });
        }
        return;
      }
      
      // Check transId (legacy or alternative field)
      const q2 = query(collection(db, col), where('transId', '==', String(transactionId)));
      const snap2 = await getDocs(q2);
      if (!snap2.empty) {
        for (const d of snap2.docs) {
          await updateDoc(doc(db, col, d.id), { paymentStatus: newStatus });
        }
        return;
      }
    }
  };

  // Add capture payment logic
  const captureTransaction = async (transaction) => {
    const amount = getTransactionAmount(transaction);
    if (!window.confirm(`Capture payment for transaction ${transaction.transId} ($${formatCurrency(amount)})?`)) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/capture-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId: transaction.transId, amount: amount })
      });
      const data = await response.json();
      if (data.success) {
        alert('Payment captured successfully!');
        await updateFirestoreStatus(transaction.transId, 'captured');
        fetchUnsettledTransactions();
      } else {
        setError(data.message || 'Capture failed');
      }
    } catch (err) {
      setError('Failed to capture payment');
    } finally {
      setLoading(false);
    }
  };

  // Void transaction
  const voidTransaction = async (transactionId, campaignId) => {
    if (!window.confirm('Are you sure you want to void this transaction?')) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/void-transaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId, campaignId })
      });
      const data = await response.json();
      if (data.success) {
        alert(`Transaction ${transactionId} voided successfully`);
        await updateFirestoreStatus(transactionId, 'voided');
        fetchUnsettledTransactions();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to void transaction');
    } finally {
      setLoading(false);
    }
  };

  // Refund transaction
  const refundTransaction = async (transactionId, maxAmount, campaignId) => {
    const amount = window.prompt(`Enter refund amount (max: $${formatCurrency(maxAmount)}):`);
    if (!amount || isNaN(amount) || parseFloat(amount) > maxAmount) {
      alert('Invalid amount or amount exceeds maximum');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/refund-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId, amount: parseFloat(amount), campaignId })
      });
      const data = await response.json();
      if (data.success) {
        alert(`Refund of $${amount} processed successfully`);
        await updateFirestoreStatus(transactionId, 'refunded');
        fetchUnsettledTransactions();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to process refund');
    } finally {
      setLoading(false);
    }
  };

  // Add totals row to batches table
  const totals = batches.reduce((acc, batch) => {
    acc.chargeAmount += batch.statistics?.chargeAmount || 0;
    acc.chargeCount += batch.statistics?.chargeCount || 0;
    acc.refundAmount += batch.statistics?.refundAmount || 0;
    acc.refundCount += batch.statistics?.refundCount || 0;
    acc.voidCount += batch.statistics?.voidCount || 0;
    acc.declineCount += batch.statistics?.declineCount || 0;
    acc.errorCount += batch.statistics?.errorCount || 0;
    return acc;
  }, { chargeAmount: 0, chargeCount: 0, refundAmount: 0, refundCount: 0, voidCount: 0, declineCount: 0, errorCount: 0 });

  // Monthly summary for batches tab (uses current fetched batches)
  const monthlyBatchesSummary = useMemo(() => {
    const map = {};
    batches.forEach(b => {
      const ts = b.settlementTimeUTC || b.settlementTimeLocal || '';
      if (!ts || ts.length < 7) return;
      const key = ts.slice(0, 7);
      if (!map[key]) map[key] = { chargeAmount: 0, refundAmount: 0, chargeCount: 0, refundCount: 0, voidCount: 0, declineCount: 0, errorCount: 0 };
      map[key].chargeAmount += b.statistics?.chargeAmount || 0;
      map[key].refundAmount += b.statistics?.refundAmount || 0;
      map[key].chargeCount += b.statistics?.chargeCount || 0;
      map[key].refundCount += b.statistics?.refundCount || 0;
      map[key].voidCount += b.statistics?.voidCount || 0;
      map[key].declineCount += b.statistics?.declineCount || 0;
      map[key].errorCount += b.statistics?.errorCount || 0;
    });
    const keys = Object.keys(map).sort();
    const rows = keys.map(k => {
      const s = map[k];
      const [y, m] = k.split('-');
      const label = new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, { month: 'short', year: 'numeric' });
      return {
        monthKey: k,
        monthLabel: label,
        chargeAmount: s.chargeAmount,
        refundAmount: s.refundAmount,
        chargeCount: s.chargeCount,
        refundCount: s.refundCount,
        voidCount: s.voidCount,
        declineCount: s.declineCount,
        errorCount: s.errorCount,
        net: (s.chargeAmount || 0) - (s.refundAmount || 0)
      };
    });
    const totals = rows.reduce((acc, r) => {
      acc.chargeAmount += r.chargeAmount;
      acc.refundAmount += r.refundAmount;
      acc.chargeCount += r.chargeCount;
      acc.refundCount += r.refundCount;
      acc.voidCount += r.voidCount;
      acc.declineCount += r.declineCount;
      acc.errorCount += r.errorCount;
      return acc;
    }, { chargeAmount: 0, refundAmount: 0, chargeCount: 0, refundCount: 0, voidCount: 0, declineCount: 0, errorCount: 0 });
    return { rows, totals };
  }, [batches]);

  // Batches tab: On the way to bank (based on current fetched batches)
  const onTheWayToBankForList = useMemo(() => {
    const accum = {
      batchCount: 0,
      chargeAmount: 0,
      refundAmount: 0,
      net: 0,
    };
    batches.forEach(b => {
      const id = String(b.batchId);
      const receipt = batchReceipts[id];
      if (!receipt || !receipt.received) {
        const stats = b.statistics || {};
        accum.batchCount += 1;
        accum.chargeAmount += stats.chargeAmount || 0;
        accum.refundAmount += stats.refundAmount || 0;
      }
    });
    accum.net = accum.chargeAmount - accum.refundAmount;
    return accum;
  }, [batches, batchReceipts]);

  // Helper to get the correct amount for unsettled transactions
  const getTransactionAmount = (transaction) => {
    if (transaction.amount && transaction.amount > 0) return transaction.amount;
    if (transaction.settleAmount && transaction.settleAmount > 0) return transaction.settleAmount;
    if (transaction.authAmount && transaction.authAmount > 0) return transaction.authAmount;
    return 0;
  };

  // Add totals for unsettled transactions (excluding declined, subtracting refundPendingSettlement)
  const unsettledTotals = unsettledTransactions.reduce((acc, transaction) => {
    const status = transaction.transactionStatus;
    if (status !== 'declined') {
      const amount = getTransactionAmount(transaction);
      
      // Only sum valid financial statuses
      if (status === 'capturedPendingSettlement' || status === 'authorizedPendingCapture') {
        acc.amount += amount;
      } else if (status === 'refundPendingSettlement') {
        acc.amount -= amount;
      }

      if (status === 'capturedPendingSettlement') {
        acc.capturedAmount += amount;
        acc.capturedCount += 1;
      }

      acc.count += 1;
    }
    return acc;
  }, { amount: 0, count: 0, capturedAmount: 0, capturedCount: 0 });

  // ---- Overview data (graphs) ----
  const lastNDates = (n) => {
    const days = [];
    const today = new Date();
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({ key, label: new Date(key).toLocaleDateString() });
    }
    return days;
  };

  const groupSettlementsByDay = useMemo(() => {
    const map = {};
    overviewBatches.forEach(b => {
      const key = (b.settlementTimeUTC || '').slice(0, 10);
      if (!key) return;
      if (!map[key]) map[key] = { chargeAmount: 0, refundAmount: 0, chargeCount: 0, refundCount: 0 };
      map[key].chargeAmount += b.statistics?.chargeAmount || 0;
      map[key].refundAmount += b.statistics?.refundAmount || 0;
      map[key].chargeCount += b.statistics?.chargeCount || 0;
      map[key].refundCount += b.statistics?.refundCount || 0;
    });
    return map;
  }, [overviewBatches]);

  const groupUnsettledByDay = useMemo(() => {
    const map = {};
    unsettledTransactions.forEach(t => {
      const key = (t.submitTimeUTC || '').slice(0, 10);
      if (!key) return;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [unsettledTransactions]);

  const groupOrdersByDay = useMemo(() => {
    const map = {};
    overviewOrders.forEach(o => {
      const key = o.date.toISOString().slice(0, 10);
      if (!map[key]) map[key] = { count: 0, charges: 0, refunds: 0 };
      map[key].count += 1;
      if (o.paymentStatus === 'captured') map[key].charges += o.totalPrice || 0;
      if (o.paymentStatus === 'refunded') map[key].refunds += o.totalPrice || 0;
    });
    return map;
  }, [overviewOrders]);

  const dailyTransactionsData = useMemo(() => {
    return lastNDates(30).map(d => {
      const settledRaw = (groupSettlementsByDay[d.key]?.chargeCount || 0) + (groupSettlementsByDay[d.key]?.refundCount || 0);
      const ordersCount = groupOrdersByDay[d.key]?.count || 0;
      const settled = settledRaw > 0 ? settledRaw : ordersCount;
      const unsettled = groupUnsettledByDay[d.key] || 0;
      return { date: d.label, settled, unsettled };
    });
  }, [groupSettlementsByDay, groupUnsettledByDay, groupOrdersByDay]);

  const dailySettlementAmounts = useMemo(() => {
    return lastNDates(30).map(d => {
      const s = groupSettlementsByDay[d.key] || { chargeAmount: 0, refundAmount: 0 };
      let charges = s.chargeAmount || 0;
      let refunds = s.refundAmount || 0;
      if (charges === 0 && refunds === 0) {
        // fallback to orders
        charges = groupOrdersByDay[d.key]?.charges || 0;
        refunds = groupOrdersByDay[d.key]?.refunds || 0;
      }
      const net = charges - refunds;
      return { date: d.label, charges: +charges.toFixed(2), refunds: +refunds.toFixed(2), net: +net.toFixed(2) };
    });
  }, [groupSettlementsByDay, groupOrdersByDay]);

  const monthlySettlementAmounts = useMemo(() => {
    const months = monthKeysBetween(overviewRange.startDate, overviewRange.endDate);
    const map = {};
    overviewBatches.forEach(b => {
      const ts = b.settlementTimeUTC || b.settlementTimeLocal || '';
      if (!ts || ts.length < 7) return;
      const key = ts.slice(0, 7); // YYYY-MM
      if (!map[key]) map[key] = { chargeAmount: 0, refundAmount: 0, unsettledAmount: 0 };
      map[key].chargeAmount += b.statistics?.chargeAmount || 0;
      map[key].refundAmount += b.statistics?.refundAmount || 0;
    });
    // Unsettled (authorized / pending settlement) by submit month — yellow stacked on charges
    unsettledTransactions.forEach((t) => {
      const status = t.transactionStatus;
      if (status === 'declined') return;
      const ts = t.submitTimeUTC || t.submitTimeLocal || '';
      if (!ts || ts.length < 7) return;
      const key = ts.slice(0, 7);
      if (!map[key]) map[key] = { chargeAmount: 0, refundAmount: 0, unsettledAmount: 0 };
      const amount = getTransactionAmount(t);
      if (status === 'capturedPendingSettlement' || status === 'authorizedPendingCapture') {
        map[key].unsettledAmount += amount;
      } else if (status === 'refundPendingSettlement') {
        map[key].unsettledAmount -= amount;
      }
    });
    return months.map(m => {
      const s = map[m.key] || { chargeAmount: 0, refundAmount: 0, unsettledAmount: 0 };
      const charges = s.chargeAmount || 0;
      const refunds = s.refundAmount || 0;
      const unsettled = s.unsettledAmount || 0;
      const net = charges + unsettled - refunds;
      return {
        month: m.label,
        charges: +charges.toFixed(2),
        refunds: +refunds.toFixed(2),
        unsettled: +Math.max(0, unsettled).toFixed(2),
        net: +net.toFixed(2),
      };
    });
  }, [overviewBatches, overviewRange, unsettledTransactions]);

  // Overview monthly summary table (months in selected overview range)
  const overviewMonthlySummary = useMemo(() => {
    const months = monthKeysBetween(overviewRange.startDate, overviewRange.endDate);
    const map = {};
    overviewBatches.forEach(b => {
      const ts = b.settlementTimeUTC || b.settlementTimeLocal || '';
      if (!ts || ts.length < 7) return;
      const key = ts.slice(0, 7);
      if (!map[key]) map[key] = { chargeAmount: 0, refundAmount: 0, unsettledAmount: 0, chargeCount: 0, refundCount: 0, voidCount: 0, declineCount: 0, errorCount: 0 };
      map[key].chargeAmount += b.statistics?.chargeAmount || 0;
      map[key].refundAmount += b.statistics?.refundAmount || 0;
      map[key].chargeCount += b.statistics?.chargeCount || 0;
      map[key].refundCount += b.statistics?.refundCount || 0;
      map[key].voidCount += b.statistics?.voidCount || 0;
      map[key].declineCount += b.statistics?.declineCount || 0;
      map[key].errorCount += b.statistics?.errorCount || 0;
    });
    unsettledTransactions.forEach((t) => {
      const status = t.transactionStatus;
      if (status === 'declined') return;
      const ts = t.submitTimeUTC || t.submitTimeLocal || '';
      if (!ts || ts.length < 7) return;
      const key = ts.slice(0, 7);
      if (!map[key]) map[key] = { chargeAmount: 0, refundAmount: 0, unsettledAmount: 0, chargeCount: 0, refundCount: 0, voidCount: 0, declineCount: 0, errorCount: 0 };
      const amount = getTransactionAmount(t);
      if (status === 'capturedPendingSettlement' || status === 'authorizedPendingCapture') {
        map[key].unsettledAmount += amount;
      } else if (status === 'refundPendingSettlement') {
        map[key].unsettledAmount -= amount;
      }
    });
    const rows = months.map(m => {
      const s = map[m.key] || { chargeAmount: 0, refundAmount: 0, unsettledAmount: 0, chargeCount: 0, refundCount: 0, voidCount: 0, declineCount: 0, errorCount: 0 };
      const unsettledAmount = s.unsettledAmount || 0;
      const net = (s.chargeAmount || 0) + unsettledAmount - (s.refundAmount || 0);
      return { monthKey: m.key, monthLabel: m.label, ...s, unsettledAmount, net };
    });
    const totals = rows.reduce((acc, r) => {
      acc.chargeAmount += r.chargeAmount;
      acc.refundAmount += r.refundAmount;
      acc.unsettledAmount += r.unsettledAmount || 0;
      acc.chargeCount += r.chargeCount;
      acc.refundCount += r.refundCount;
      acc.voidCount += r.voidCount;
      acc.declineCount += r.declineCount;
      acc.errorCount += r.errorCount;
      return acc;
    }, { chargeAmount: 0, refundAmount: 0, unsettledAmount: 0, chargeCount: 0, refundCount: 0, voidCount: 0, declineCount: 0, errorCount: 0 });
    totals.net = (totals.chargeAmount || 0) + (totals.unsettledAmount || 0) - (totals.refundAmount || 0);
    return { rows, totals };
  }, [overviewBatches, overviewRange, unsettledTransactions]);

  const overviewTotalRevenue = overviewMonthlySummary.totals;

  // Overview: On the way to bank (batches settled but not marked received)
  const onTheWayToBank = useMemo(() => {
    const accum = {
      batchCount: 0,
      chargeAmount: 0,
      refundAmount: 0,
      net: 0,
    };
    overviewBatches.forEach(b => {
      const id = String(b.batchId);
      const receipt = batchReceipts[id];
      if (!receipt || !receipt.received) {
        const stats = b.statistics || {};
        accum.batchCount += 1;
        accum.chargeAmount += stats.chargeAmount || 0;
        accum.refundAmount += stats.refundAmount || 0;
      }
    });
    accum.net = accum.chargeAmount - accum.refundAmount;
    return accum;
  }, [overviewBatches, batchReceipts]);

  const totalOnTheWay = useMemo(() => ({
    net: (onTheWayToBank.net || 0) + (unsettledTotals.amount || 0),
    settledNotReceived: onTheWayToBank.net || 0,
    unsettled: unsettledTotals.amount || 0,
    batchCount: onTheWayToBank.batchCount || 0,
    unsettledCount: unsettledTotals.count || 0,
  }), [onTheWayToBank, unsettledTotals]);

  return (
    <Layout>
      <div className="f-page-enter">
        {/* Fluent Header */}
        <div className="f-header">
          <div>
            <h1 className="f-title">Financial Management</h1>
            <p className="f-subtitle">Track settlements, customer spend by campaign vs takeover, wallets, and DSP billing invoices</p>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', border: '1px solid var(--ms-border)', borderRadius: 2, overflow: 'hidden' }}>
              <button 
                className={`f-button ${tab === 'overview' ? '' : 'f-button-secondary'}`} 
                style={{ border: 'none', borderRadius: 0 }}
                onClick={() => setTab('overview')}
              >
                Overview
              </button>
              <button
                className={`f-button ${tab === 'spenders' ? '' : 'f-button-secondary'}`}
                style={{ border: 'none', borderRadius: 0 }}
                onClick={() => setTab('spenders')}
              >
                Spenders
              </button>
              <button 
                className={`f-button ${tab === 'batches' ? '' : 'f-button-secondary'}`} 
                style={{ border: 'none', borderRadius: 0 }}
                onClick={() => setTab('batches')}
              >
                Batches
              </button>
              <button 
                className={`f-button ${tab === 'unsettled' ? '' : 'f-button-secondary'}`} 
                style={{ border: 'none', borderRadius: 0 }}
                onClick={() => setTab('unsettled')}
              >
                Unsettled
              </button>
              <button
                className={`f-button ${tab === 'wallets' ? '' : 'f-button-secondary'}`}
                style={{ border: 'none', borderRadius: 0 }}
                onClick={() => setTab('wallets')}
              >
                Wallets
              </button>
              <button 
                className={`f-button ${tab === 'reconcile' ? '' : 'f-button-secondary'}`} 
                style={{ border: 'none', borderRadius: 0 }}
                onClick={() => setTab('reconcile')}
              >
                DSP Reconciliation
              </button>
              <button
                className={`f-button ${tab === 'invoices' ? '' : 'f-button-secondary'}`}
                style={{ border: 'none', borderRadius: 0 }}
                onClick={() => setTab('invoices')}
              >
                DSP Invoices
              </button>
            </div>
          </div>
        </div>

        <div className="f-page-content" style={{ padding: '0 32px 32px 32px' }}>
          {tab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Range controls */}
              <div className="f-card" style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, marginRight: 4 }}>Date range</span>
                  <button type="button" className="f-button f-button-secondary" onClick={() => applyOverviewPreset('3m')}>3 mo</button>
                  <button type="button" className="f-button f-button-secondary" onClick={() => applyOverviewPreset('12m')}>12 mo</button>
                  <button type="button" className="f-button f-button-secondary" onClick={() => applyOverviewPreset('24m')}>24 mo</button>
                  <button type="button" className="f-button f-button-secondary" onClick={() => applyOverviewPreset('ytd')}>YTD</button>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="date"
                    className="f-input"
                    value={overviewRange.startDate}
                    onChange={(e) => setOverviewRange((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                  <input
                    type="date"
                    className="f-input"
                    value={overviewRange.endDate}
                    onChange={(e) => setOverviewRange((prev) => ({ ...prev, endDate: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="f-button"
                    disabled={overviewLoading || !overviewRange.startDate || !overviewRange.endDate}
                    onClick={() => fetchBatchesForOverview(overviewRange)}
                  >
                    {overviewLoading ? 'Loading…' : 'Fetch'}
                  </button>
                </div>
              </div>

              {overviewError && (
                <div style={{ color: '#d13438', fontSize: 14 }}>{overviewError}</div>
              )}

              {/* Summary Tiles */}
              <div className="f-grid">
                <div className="f-card f-stat-card">
                  <span className="f-stat-label">Total Revenue (selected range)</span>
                  <span className="f-stat-value" style={{ color: '#107c10' }}>
                    {overviewLoading ? '…' : `$${formatCurrency(overviewTotalRevenue.net)}`}
                  </span>
                  <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                    ${formatCurrency(overviewTotalRevenue.chargeAmount)} charges • ${formatCurrency(overviewTotalRevenue.unsettledAmount)} unsettled • -${formatCurrency(overviewTotalRevenue.refundAmount)} refunds • {overviewBatches.length} batches
                  </div>
                </div>
                <div className="f-card f-stat-card">
                  <span className="f-stat-label">Total on the way</span>
                  <span className="f-stat-value" style={{ color: '#0b7fd4' }}>${formatCurrency(totalOnTheWay.net)}</span>
                  <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                    ${formatCurrency(totalOnTheWay.settledNotReceived)} not received + ${formatCurrency(totalOnTheWay.unsettled)} unsettled
                  </div>
                </div>
                <div className="f-card f-stat-card">
                  <span className="f-stat-label">On the way to bank (not received)</span>
                  <span className="f-stat-value">${formatCurrency(onTheWayToBank.net)}</span>
                  <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                    {onTheWayToBank.batchCount} batches • ${formatCurrency(onTheWayToBank.chargeAmount)} charges • -${formatCurrency(onTheWayToBank.refundAmount)} refunds
                  </div>
                </div>
                <div className="f-card f-stat-card">
                  <span className="f-stat-label">Unsettled (Authorized)</span>
                  <span className="f-stat-value" style={{ color: '#f59e0b' }}>${formatCurrency(unsettledTotals.amount)}</span>
                  <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{unsettledTotals.count} transactions pending</div>
                </div>
              </div>

              {/* Monthly Performance chart + table */}
              <div className="f-card" style={{ padding: 0 }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ms-border-light)', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span>Monthly Performance</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ms-text-secondary)' }}>
                    {overviewRange.startDate} → {overviewRange.endDate}
                  </span>
                </div>
                <div style={{ padding: '16px 24px 8px', height: 320, position: 'relative' }}>
                  {overviewLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', zIndex: 1, fontSize: 14, color: 'var(--ms-text-secondary)' }}>
                      Loading Authorize.net batches…
                    </div>
                  )}
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthlySettlementAmounts}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edebe9" />
                      <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} interval={0} angle={-30} textAnchor="end" height={50} />
                      <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} width={72} />
                      <Tooltip
                        formatter={(value, name) => [
                          `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                          name,
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="charges" stackId="charges" fill="#107c10" name="Charges" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="unsettled" stackId="charges" fill="#f59e0b" name="Unsettled" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="refunds" fill="#d13438" name="Refunds" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="net" stroke="#0b7fd4" strokeWidth={2} dot={{ r: 3 }} name="Net Revenue" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ overflowX: 'auto', borderTop: '1px solid var(--ms-border-light)' }}>
                  <table className="f-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Charges</th>
                        <th>Unsettled</th>
                        <th>Refunds</th>
                        <th>Net Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overviewMonthlySummary.rows.map(r => (
                        <tr key={r.monthKey}>
                          <td>{r.monthLabel}</td>
                          <td style={{ color: '#107c10' }}>${formatCurrency(r.chargeAmount)}</td>
                          <td style={{ color: '#f59e0b' }}>${formatCurrency(r.unsettledAmount)}</td>
                          <td style={{ color: '#d13438' }}>-${formatCurrency(r.refundAmount)}</td>
                          <td style={{ fontWeight: 600 }}>${formatCurrency(r.net)}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700, background: 'var(--ms-bg-secondary, #f3f2f1)' }}>
                        <td>Total</td>
                        <td style={{ color: '#107c10' }}>${formatCurrency(overviewTotalRevenue.chargeAmount)}</td>
                        <td style={{ color: '#f59e0b' }}>${formatCurrency(overviewTotalRevenue.unsettledAmount)}</td>
                        <td style={{ color: '#d13438' }}>-${formatCurrency(overviewTotalRevenue.refundAmount)}</td>
                        <td>${formatCurrency(overviewTotalRevenue.net)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === 'spenders' && <RevenueByCustomer />}

          {tab === 'batches' && (
            <div className="f-card" style={{ padding: 0 }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <span style={{ fontWeight: 600 }}>Settled Batches</span>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <input type="date" className="f-input" value={dateRange.startDate} onChange={e => setDateRange(prev => ({ ...prev, startDate: e.target.value }))} />
                  <input type="date" className="f-input" value={dateRange.endDate} onChange={e => setDateRange(prev => ({ ...prev, endDate: e.target.value }))} />
                  <button className="f-button" onClick={() => fetchBatches()}>Fetch</button>
                </div>
              </div>
              {/* On the way to bank for the currently loaded date range */}
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ms-border-light)' }}>
                <div className="f-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                  <div className="f-card f-stat-card" style={{ margin: 0 }}>
                    <span className="f-stat-label">On the way to bank (not received)</span>
                    <span className="f-stat-value">${formatCurrency(onTheWayToBankForList.net)}</span>
                    <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                      {onTheWayToBankForList.batchCount} batches
                    </div>
                  </div>
                  <div className="f-card f-stat-card" style={{ margin: 0 }}>
                    <span className="f-stat-label">Charges (in transit)</span>
                    <span className="f-stat-value" style={{ color: '#107c10' }}>${formatCurrency(onTheWayToBankForList.chargeAmount)}</span>
                  </div>
                  <div className="f-card f-stat-card" style={{ margin: 0 }}>
                    <span className="f-stat-label">Refunds (in transit)</span>
                    <span className="f-stat-value" style={{ color: '#d13438' }}>-${formatCurrency(onTheWayToBankForList.refundAmount)}</span>
                  </div>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="f-table">
                  <thead>
                    <tr>
                      <th>Batch ID</th>
                      <th>Settlement Date</th>
                      <th>Charges</th>
                      <th>Refunds</th>
                      <th>Status</th>
                      <th>Bank Receipt</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map(batch => (
                      <tr key={batch.batchId}>
                        <td>{batch.batchId}</td>
                        <td>{formatDate(batch.settlementTimeUTC)}</td>
                        <td style={{ color: '#107c10' }}>${formatCurrency(batch.statistics?.chargeAmount)}</td>
                        <td style={{ color: '#d13438' }}>-${formatCurrency(batch.statistics?.refundAmount)}</td>
                        <td>{batch.settlementState}</td>
                        <td>
                          {batchReceipts[String(batch.batchId)]?.received ? (
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 2, background: '#e6f4ea', color: '#107c10' }}>
                              Received
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 2, background: 'var(--ms-bg)', color: 'var(--ms-text-secondary)' }}>
                              In transit
                            </span>
                          )}
                        </td>
                        <td>
                          <button 
                            className={`f-button ${batchReceipts[String(batch.batchId)] ? 'f-button-secondary' : ''}`}
                            style={{ height: 24, fontSize: 11 }}
                            onClick={() => markBatchReceived(batch)}
                            disabled={!!batchReceipts[String(batch.batchId)]}
                          >
                            {batchReceipts[String(batch.batchId)] ? 'Received' : 'Mark Received'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'unsettled' && (
            <div className="f-card" style={{ padding: 0 }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>Recent Unsettled Transactions</span>
                <button className="f-button f-button-secondary" onClick={fetchUnsettledTransactions}>Refresh</button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="f-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unsettledTransactions.map(t => (
                      <tr key={t.transId}>
                        <td>{dayjs(t.submitTimeUTC).format('MMM D, YYYY')}</td>
                        <td>{t.firstName} {t.lastName}</td>
                        <td style={{ fontWeight: 600 }}>${formatCurrency(getTransactionAmount(t))}</td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: 2, fontSize: 11, background: 'var(--ms-bg)' }}>{t.transactionStatus}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {t.transactionStatus === 'authorizedPendingCapture' && (
                              <button className="f-button" style={{ height: 24, fontSize: 11, background: '#107c10' }} onClick={() => captureTransaction(t)}>Capture</button>
                            )}
                            <button className="f-button f-button-secondary" style={{ height: 24, fontSize: 11, color: '#d13438' }} onClick={() => voidTransaction(t.transId)}>Void</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 600, borderTop: '2px solid var(--ms-border-light)' }}>
                      <td colSpan={2} style={{ textAlign: 'right', paddingRight: 16 }}>Total Net (Captured & Authorized):</td>
                      <td style={{ color: '#107c10' }}>${formatCurrency(unsettledTotals.amount)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {tab === 'wallets' && (
            <div className="f-card f-wallet-panel" style={{ padding: 20 }}>
              {walletError && (
                <div className="f-wallet-msg f-wallet-msg-err">{walletError}</div>
              )}
              {adjustMessage && (
                <div className="f-wallet-msg f-wallet-msg-ok">{adjustMessage}</div>
              )}

              <p className="f-wallet-intro">
                Lists users with a wallet account: positive balance, or <strong>$0</strong> but a prior{' '}
                <strong>wallet → campaign budget</strong> move (ledger type{' '}
                <code>campaign_budget_topup</code>). New moves set <code>hasCampaignBudgetTopUp</code> on the account
                doc; older accounts are detected with a capped ledger probe (
                <code>ADMIN_WALLET_LEGACY_PROBE_MAX</code>, default 40,000).
              </p>

              {walletScanUsersScanned != null && !walletLoading && (
                <p className="f-wallet-scan-meta">
                  Scanned {walletScanUsersScanned.toLocaleString()} user docs · {walletRows.length} listed
                  {walletScanTruncated && (
                    <span className="f-wallet-warn"> · <strong>Stopped at scan cap.</strong></span>
                  )}
                  {walletLegacyProbeTruncated && (
                    <span className="f-wallet-warn">
                      {' '}
                      · <strong>Legacy campaign-transfer probe capped — raise ADMIN_WALLET_LEGACY_PROBE_MAX.</strong>
                    </span>
                  )}
                </p>
              )}

              <div className="f-wallet-toolbar">
                <button type="button" className="f-button" disabled={walletLoading} onClick={fetchWalletBalances}>
                  {walletLoading ? 'Loading…' : 'Refresh list'}
                </button>
                <div className="f-wallet-lookup">
                  <input
                    type="text"
                    className="f-input f-wallet-uid-inp"
                    value={walletLookupUid}
                    onChange={(e) => setWalletLookupUid(e.target.value)}
                    placeholder="Firebase UID (lookup ledger / adjust)"
                    autoComplete="off"
                  />
                  <button type="button" className="f-button f-button-secondary" onClick={loadWalletLookup}>
                    Load UID
                  </button>
                </div>
              </div>

              <div className="f-wallet-split">
                <div className="f-wallet-col">
                  <h4 className="f-wallet-heading">Wallet users</h4>
                  {walletLoading ? (
                    <p className="f-wallet-muted">Loading…</p>
                  ) : (
                    <div className="f-wallet-scroll">
                      <table className="f-table f-table-compact">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Balance</th>
                            <th>Notes</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {walletRows.map((w) => (
                            <tr
                              key={w.userId}
                              className={selectedWalletUserId === w.userId ? 'f-row-selected' : ''}
                            >
                              <td>
                                <div className="f-wallet-mono">{w.userId}</div>
                                {w.email && <div className="f-wallet-sub">{w.email}</div>}
                                {!w.email && w.displayName && <div className="f-wallet-sub">{w.displayName}</div>}
                              </td>
                              <td>{formatWalletUsd(w.balanceCents)}</td>
                              <td>
                                {w.hadCampaignTransfer ? (
                                  <span className="f-pill">Campaign wallet transfer</span>
                                ) : (
                                  <span className="f-wallet-muted">—</span>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="f-button f-button-secondary"
                                  style={{ height: 24, fontSize: 11 }}
                                  onClick={() => fetchWalletLedgerForUser(w.userId)}
                                >
                                  Ledger
                                </button>
                              </td>
                            </tr>
                          ))}
                          {!walletRows.length && (
                            <tr>
                              <td colSpan={4} className="f-wallet-muted">
                                No matching wallet users in this scan.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="f-wallet-col f-wallet-col-detail">
                  <h4 className="f-wallet-heading">Ledger &amp; adjustment</h4>
                  {selectedWalletUserId ? (
                    <>
                      <p className="f-wallet-meta-line">
                        <span className="f-wallet-mono">{selectedWalletUserId}</span>
                        {' · Balance '}
                        <strong>{formatWalletUsd(walletLedgerMeta.balanceCents)}</strong>
                        {walletLedgerMeta.email && <span> · {walletLedgerMeta.email}</span>}
                      </p>

                      <div className="f-adjust-card">
                        <h5 className="f-wallet-heading-sm">Admin adjustment</h5>
                        <p className="f-wallet-muted f-wallet-small">
                          Server enforces Firebase <code>admin</code> on your user doc, idempotency, max size (
                          <code>WALLET_ADMIN_ADJUST_MAX_ABS_USD</code>, default $100,000), and blocks debits below zero.
                        </p>
                        <div className="f-adjust-row">
                          <label className="f-adjust-seg">
                            <input
                              type="radio"
                              name="adjustDirection"
                              checked={adjustDirection === 'credit'}
                              onChange={() => setAdjustDirection('credit')}
                            />
                            Credit
                          </label>
                          <label className="f-adjust-seg">
                            <input
                              type="radio"
                              name="adjustDirection"
                              checked={adjustDirection === 'debit'}
                              onChange={() => setAdjustDirection('debit')}
                            />
                            Debit
                          </label>
                        </div>
                        <label className="f-wallet-block-label">Amount (USD)</label>
                        <input
                          type="text"
                          className="f-input f-wallet-block-inp"
                          value={adjustUsd}
                          onChange={(e) => setAdjustUsd(e.target.value)}
                          placeholder="0.00"
                          inputMode="decimal"
                        />
                        <label className="f-wallet-block-label">Reason (audit — min 8 characters)</label>
                        <textarea
                          className="f-wallet-ta"
                          rows={3}
                          value={adjustReason}
                          onChange={(e) => setAdjustReason(e.target.value)}
                          placeholder="e.g. Goodwill credit for outage INV-123"
                        />
                        <button
                          type="button"
                          className={`f-button ${adjustDirection === 'debit' ? 'f-button-warn' : ''}`}
                          disabled={adjustSubmitting || !selectedWalletUserId}
                          onClick={submitWalletAdjust}
                        >
                          {adjustSubmitting ? 'Applying…' : 'Apply adjustment'}
                        </button>
                      </div>

                      <h5 className="f-wallet-heading-sm f-ledger-h">Movement history</h5>
                      {walletLedgerLoading ? (
                        <p className="f-wallet-muted">Loading ledger…</p>
                      ) : (
                        <div className="f-wallet-scroll">
                          <table className="f-table f-table-compact f-table-ledger">
                            <thead>
                              <tr>
                                <th>When</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>After</th>
                                <th>Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {walletLedger.map((row) => (
                                <tr key={row.id}>
                                  <td className="f-wallet-nowrap">
                                    {(row.completedAt || row.createdAt || '').slice(0, 19) || '—'}
                                  </td>
                                  <td>{ledgerTypeLabel(row.type)}</td>
                                  <td
                                    className={
                                      (row.amountCents || 0) < 0
                                        ? 'f-ledger-neg'
                                        : (row.amountCents || 0) > 0
                                          ? 'f-ledger-pos'
                                          : ''
                                    }
                                  >
                                    {formatWalletUsd(row.amountCents ?? undefined)}
                                  </td>
                                  <td>{formatWalletUsd(row.balanceAfterCents ?? undefined)}</td>
                                  <td className="f-ledger-detail-cell">
                                    {row.campaignId && (
                                      <a
                                        className="f-wallet-mono f-campaign-link"
                                        href={`${CAMPAIGN_APP_BASE}/${encodeURIComponent(row.campaignId)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {row.campaignId}
                                      </a>
                                    )}
                                    {row.reason && <span className="f-ledger-reason">{row.reason}</span>}
                                    {row.adminUid && (
                                      <span className="f-wallet-sub">by {row.adminUid.slice(0, 8)}…</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                              {!walletLedger.length && (
                                <tr>
                                  <td colSpan={5} className="f-wallet-muted">
                                    No ledger rows (new wallet or no activity yet).
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="f-wallet-muted">
                      Select a user from the list or load a UID to view history and adjust balance.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'invoices' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {invoicesError && (
                <div className="f-wallet-msg f-wallet-msg-err">{invoicesError}</div>
              )}

              <div className="f-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                <div className="f-card f-stat-card" style={{ margin: 0 }}>
                  <span className="f-stat-label">Total outstanding</span>
                  <span className="f-stat-value" style={{ color: '#0369a1' }}>
                    ${formatCurrency(dspInvoiceSummary.totalOutstanding)}
                  </span>
                  <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                    {dspInvoiceSummary.outstandingCount} open invoice{dspInvoiceSummary.outstandingCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="f-card f-stat-card" style={{ margin: 0 }}>
                  <span className="f-stat-label">Past due</span>
                  <span className="f-stat-value" style={{ color: '#d13438' }}>
                    {dspInvoiceSummary.pastDueCount}
                  </span>
                  <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                    {INVOICE_DUE_DAYS} days after period end
                  </div>
                </div>
                <div className="f-card f-stat-card" style={{ margin: 0 }}>
                  <span className="f-stat-label">DSPs with balance</span>
                  <span className="f-stat-value">{dspInvoiceSummary.dspRows.length}</span>
                  <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                    {dspInvoiceSummary.openCount} not yet past due
                  </div>
                </div>
              </div>

              {dspInvoiceSummary.dspRows.length > 0 && (
                <div className="f-card" style={{ padding: 0 }}>
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ms-border-light)', fontWeight: 600, fontSize: 14 }}>
                    Amount owed by DSP
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="f-table">
                      <thead>
                        <tr>
                          <th>DSP</th>
                          <th>Open invoices</th>
                          <th>Past due</th>
                          <th>Total owed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dspInvoiceSummary.dspRows.map((row) => (
                          <tr key={row.dspInstanceId}>
                            <td>
                              <div style={{ fontWeight: 600 }}>{row.name}</div>
                              <div className="f-wallet-mono f-wallet-sub">{row.dspInstanceId}</div>
                            </td>
                            <td>{row.invoiceCount}</td>
                            <td style={{ color: row.pastDueCount ? '#d13438' : undefined, fontWeight: row.pastDueCount ? 600 : undefined }}>
                              {row.pastDueCount || '—'}
                            </td>
                            <td style={{ fontWeight: 700, color: '#0369a1' }}>${formatCurrency(row.outstanding)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="f-card" style={{ padding: 0 }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>All DSP monthly invoices</span>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select
                      className="f-input"
                      value={invoiceStatusFilter}
                      onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                      style={{ minWidth: 140 }}
                    >
                      <option value="outstanding">Outstanding</option>
                      <option value="all">All statuses</option>
                      <option value="open">Open</option>
                      <option value="draft">Draft</option>
                      <option value="past_due">Past due</option>
                      <option value="paid">Paid</option>
                      <option value="void">Voided</option>
                    </select>
                    <input
                      type="search"
                      className="f-input"
                      placeholder="Search DSP or invoice #"
                      value={invoiceDspSearch}
                      onChange={(e) => setInvoiceDspSearch(e.target.value)}
                      style={{ minWidth: 200 }}
                    />
                    <button type="button" className="f-button f-button-secondary" disabled={invoicesLoading} onClick={fetchDspInvoices}>
                      {invoicesLoading ? 'Loading…' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {invoicesLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--ms-text-secondary)' }}>Loading invoices…</div>
                ) : filteredDspInvoices.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--ms-text-secondary)', fontSize: 14 }}>
                    No invoices match this filter.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="f-table">
                      <thead>
                        <tr>
                          <th>Invoice</th>
                          <th>DSP</th>
                          <th>Period</th>
                          <th>Amount</th>
                          <th>Due</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDspInvoices.map((inv) => {
                          const effective = getEffectiveInvoiceStatus(inv);
                          const stored = getStoredInvoiceStatus(inv);
                          const due = getInvoiceDueDate(inv);
                          const busy = invoiceStatusUpdating === inv.invoiceId;
                          const pdfUrl = `${API_BASE}/api/invoices/${encodeURIComponent(inv.invoiceId)}/pdf`;
                          return (
                            <tr key={inv.invoiceId}>
                              <td>
                                <div style={{ fontWeight: 600 }}>{inv.invoiceNumber || inv.invoiceId}</div>
                                <div className="f-wallet-mono f-wallet-sub">{inv.invoiceId}</div>
                              </td>
                              <td>
                                <div>{dspInstanceNames[inv.dspInstanceId] || '—'}</div>
                                <div className="f-wallet-mono f-wallet-sub">{inv.dspInstanceId}</div>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>{formatInvoicePeriod(inv)}</td>
                              <td style={{ fontWeight: 700 }}>${formatCurrency(invoiceAmount(inv))}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {due ? due.toLocaleDateString() : '—'}
                                {effective === 'past_due' && stored !== 'past_due' && (
                                  <div className="f-wallet-sub" style={{ color: '#d13438' }}>auto past due</div>
                                )}
                              </td>
                              <td>
                                <span className={`f-inv-pill f-inv-pill--${effective.replace('_', '-')}`}>
                                  {formatInvoiceStatusLabel(effective)}
                                </span>
                                {inv.paidAt && (
                                  <div className="f-wallet-sub">Paid {formatDate(inv.paidAt)}</div>
                                )}
                              </td>
                              <td>
                                <div className="f-inv-actions">
                                  <a
                                    className="f-button f-button-secondary"
                                    style={{ height: 24, fontSize: 11, lineHeight: '22px', textDecoration: 'none' }}
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    PDF
                                  </a>
                                  {effective !== 'paid' && (
                                    <button
                                      type="button"
                                      className="f-button"
                                      style={{ height: 24, fontSize: 11, background: '#107c10' }}
                                      disabled={busy}
                                      onClick={() => updateInvoiceStatus(inv, 'paid')}
                                    >
                                      Mark paid
                                    </button>
                                  )}
                                  {isOutstandingInvoiceStatus(effective) && stored !== 'past_due' && (
                                    <button
                                      type="button"
                                      className="f-button f-button-secondary"
                                      style={{ height: 24, fontSize: 11, color: '#d13438' }}
                                      disabled={busy}
                                      onClick={() => updateInvoiceStatus(inv, 'past_due')}
                                    >
                                      Past due
                                    </button>
                                  )}
                                  {effective === 'paid' && (
                                    <button
                                      type="button"
                                      className="f-button f-button-secondary"
                                      style={{ height: 24, fontSize: 11 }}
                                      disabled={busy}
                                      onClick={() => updateInvoiceStatus(inv, 'open')}
                                    >
                                      Reopen
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <p className="f-wallet-intro" style={{ margin: 0 }}>
                Invoices live in Firestore <code>invoices</code> (generated by payment-server). Marking <strong>Paid</strong> sets{' '}
                <code>status: paid</code> and updates the customer Billing page. Past due is computed as{' '}
                {INVOICE_DUE_DAYS} days after <code>periodEndDate</code>, or set manually.
              </p>
            </div>
          )}

          {tab === 'reconcile' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase' }}>Month</label>
                  <input
                    type="month"
                    className="f-input"
                    value={reconcileMonth}
                    onChange={e => setReconcileMonth(e.target.value)}
                    style={{ width: 160 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', paddingBottom: 2 }}>
                  <button
                    className="f-button"
                    onClick={() => fetchReconciliation(reconcileMonth)}
                    disabled={reconcileLoading}
                  >
                    {reconcileLoading ? 'Loading…' : 'Run Reconciliation'}
                  </button>
                  <button
                    className="f-button f-button-secondary"
                    onClick={() => {
                      const now = new Date();
                      const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                      const key = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}`;
                      setReconcileMonth(key);
                      fetchReconciliation(key);
                    }}
                    disabled={reconcileLoading}
                  >
                    Last Month
                  </button>
                  <button
                    className="f-button f-button-secondary"
                    onClick={() => {
                      const now = new Date();
                      const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                      setReconcileMonth(key);
                      fetchReconciliation(key);
                    }}
                    disabled={reconcileLoading}
                  >
                    This Month
                  </button>
                </div>
              </div>

              {reconcileError && (
                <div style={{ padding: '10px 14px', background: '#fde7e9', borderRadius: 4, color: '#b91c1c', fontSize: 13 }}>
                  {reconcileError}
                </div>
              )}

              {reconcileFetched && !reconcileLoading && (
                <>
                  {/* Summary tiles */}
                  <div className="f-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                    <div className="f-card f-stat-card" style={{ margin: 0 }}>
                      <span className="f-stat-label">Total Revenue</span>
                      <span className="f-stat-value" style={{ color: '#0369a1' }}>${formatCurrency(reconcileTotals.revenue)}</span>
                      <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{reconcileRows.length} takeover{reconcileRows.length !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="f-card f-stat-card" style={{ margin: 0 }}>
                      <span className="f-stat-label">DSP Spend</span>
                      <span className="f-stat-value" style={{ color: '#d13438' }}>${formatCurrency(reconcileTotals.dspSpend)}</span>
                      <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{reconcileTotals.wins} wins recorded</div>
                    </div>
                    <div className="f-card f-stat-card" style={{ margin: 0 }}>
                      <span className="f-stat-label">Est. Profit</span>
                      <span className="f-stat-value" style={{ color: reconcileTotals.profit >= 0 ? '#107c10' : '#d13438' }}>
                        ${formatCurrency(reconcileTotals.profit)}
                      </span>
                    </div>
                    <div className="f-card f-stat-card" style={{ margin: 0 }}>
                      <span className="f-stat-label">Avg Margin</span>
                      <span className="f-stat-value" style={{ color: 'var(--ms-text-primary)' }}>
                        {reconcileTotals.revenue > 0
                          ? `${((reconcileTotals.profit / reconcileTotals.revenue) * 100).toFixed(1)}%`
                          : '—'}
                      </span>
                    </div>
                  </div>

                  {/* Per-takeover table */}
                  <div className="f-card" style={{ padding: 0 }}>
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ms-border-light)', fontWeight: 600, fontSize: 14 }}>
                      DSP Takeover Breakdown — {new Date(reconcileMonth + '-02').toLocaleString(undefined, { month: 'long', year: 'numeric' })}
                    </div>
                    {reconcileRows.length === 0 ? (
                      <div style={{ padding: 32, textAlign: 'center', color: 'var(--ms-text-secondary)', fontSize: 14 }}>
                        No DSP takeovers found for this month.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table className="f-table">
                          <thead>
                            <tr>
                              <th>Sign</th>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Wins</th>
                              <th>Revenue</th>
                              <th>DSP Spend</th>
                              <th>Est. Profit</th>
                              <th>Margin</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reconcileRows.map(row => {
                              const margin = row.revenue > 0 ? (row.profit / row.revenue) * 100 : null;
                              return (
                                <tr key={row.id}>
                                  <td style={{ fontWeight: 500 }}>{row.signName}</td>
                                  <td style={{ whiteSpace: 'nowrap' }}>{row.date}</td>
                                  <td>
                                    <span style={{
                                      padding: '2px 8px', borderRadius: 2, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                                      background: row.status === 'Complete' ? '#dcfce7' : row.status === 'Approved' ? '#e0f2fe' : '#f3f2f1',
                                      color: row.status === 'Complete' ? '#166534' : row.status === 'Approved' ? '#0369a1' : '#64748b'
                                    }}>
                                      {row.status}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>{row.winsCount}</td>
                                  <td style={{ textAlign: 'right', color: '#0369a1', fontWeight: 600 }}>${formatCurrency(row.revenue)}</td>
                                  <td style={{ textAlign: 'right', color: '#d13438' }}>${formatCurrency(row.dspSpend)}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 700, color: row.profit >= 0 ? '#107c10' : '#d13438' }}>
                                    ${formatCurrency(row.profit)}
                                  </td>
                                  <td style={{ textAlign: 'right', color: 'var(--ms-text-secondary)' }}>
                                    {margin !== null ? `${margin.toFixed(1)}%` : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--ms-border-light)', background: 'var(--ms-bg)' }}>
                              <td colSpan={3}>Totals</td>
                              <td style={{ textAlign: 'right' }}>{reconcileTotals.wins}</td>
                              <td style={{ textAlign: 'right', color: '#0369a1' }}>${formatCurrency(reconcileTotals.revenue)}</td>
                              <td style={{ textAlign: 'right', color: '#d13438' }}>${formatCurrency(reconcileTotals.dspSpend)}</td>
                              <td style={{ textAlign: 'right', color: reconcileTotals.profit >= 0 ? '#107c10' : '#d13438' }}>
                                ${formatCurrency(reconcileTotals.profit)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {reconcileTotals.revenue > 0
                                  ? `${((reconcileTotals.profit / reconcileTotals.revenue) * 100).toFixed(1)}%`
                                  : '—'}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}

              {!reconcileFetched && !reconcileLoading && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--ms-text-secondary)', fontSize: 14 }}>
                  Select a month and click <b>Run Reconciliation</b> to load DSP profit data.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Finances;
 